import React, { useEffect, useState } from 'react';
import {
  Package, Search, Plus, Upload, Send, Trash2, RefreshCw,
  ExternalLink, Image as ImageIcon, Loader2, Twitter, Store,
  FileText, Copy, Sparkles, Globe
} from 'lucide-react';
import { get, post, del } from '../request';
import { getAccountDetails } from '../services/api';

interface ListingPack {
  listing_title?: string;
  listing_description?: string;
  suggest_price?: number;
  original_price?: number;
  publish_keyword?: string;
  tags?: string[];
  best_time?: string;
  images_tips?: string[];
  cover_image_url?: string;
  copy_text?: string;
  manual_steps?: string[];
}

interface VirtualProduct {
  id: number;
  title: string;
  category?: string;
  price?: number;
  intro?: string;
  resource_url?: string;
  detail_image_url?: string;
  cover_image_url?: string;
  source?: string;
  source_url?: string;
  source_author?: string;
  local_path?: string;
  status?: string;
  publish_keyword?: string;
  published_card_id?: number;
  published_cookie_id?: string;
  updated_at?: string;
  listing?: ListingPack | null;
}

interface Account {
  id: string;
  nickname?: string;
  remark?: string;
  enabled?: boolean;
}

const VirtualResources: React.FC = () => {
  const [items, setItems] = useState<VirtualProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [query, setQuery] = useState('虚拟资源 源码 网盘 软件');
  const [searching, setSearching] = useState(false);
  const [publishingId, setPublishingId] = useState<number | null>(null);
  const [preparingId, setPreparingId] = useState<number | null>(null);
  const [listingId, setListingId] = useState<number | null>(null);
  const [genListingId, setGenListingId] = useState<number | null>(null);

  const [listingPreview, setListingPreview] = useState<{
    productId: number;
    listing: ListingPack;
    next_steps?: string[];
    playwright?: any;
    xianguanjia?: any;
    channel?: string;
    message?: string;
    delivery_ok?: boolean;
    publish?: any;
    xianguanjia_ok?: boolean;
  } | null>(null);
  const [msg, setMsg] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '',
    category: '本地项目',
    price: 99,
    intro: '',
    resource_url: '',
    detail_image_url: '',
    publish_keyword: '',
    local_path: '',
  });
  const [selectedCookie, setSelectedCookie] = useState('');
  const [twConfigured, setTwConfigured] = useState(false);
  const [twPreview, setTwPreview] = useState('');
  const [cookieInput, setCookieInput] = useState('');
  const [savingCookie, setSavingCookie] = useState(false);
  const [showCookieBox, setShowCookieBox] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await get<VirtualProduct[]>('/virtual-products');
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setMsg(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  const loadTwitterConfig = async () => {
    try {
      const cfg = await get<any>('/virtual-products/twitter-config');
      setTwConfigured(!!cfg?.configured);
      setTwPreview(cfg?.auth_token_preview || '');
      if (!cfg?.configured) setShowCookieBox(true);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    loadTwitterConfig();
    getAccountDetails().then((list) => {
      setAccounts(list || []);
      if (list?.length) setSelectedCookie(list[0].id);
    }).catch(() => {});
  }, []);

  const flash = (text: string) => {
    setMsg(text);
    setTimeout(() => setMsg(''), 4000);
  };

  const handleCreate = async () => {
    if (!form.title.trim()) return flash('请填写标题');
    try {
      await post('/virtual-products', {
        ...form,
        source: 'manual',
        status: 'draft',
      });
      setShowForm(false);
      setForm({ title: '', category: '本地项目', price: 99, intro: '', resource_url: '', detail_image_url: '', publish_keyword: '', local_path: '' });
      flash('已添加');
      load();
    } catch (e: any) {
      flash(e?.message || '添加失败');
    }
  };

  const handleSaveTwitterCookie = async () => {
    if (!cookieInput.trim()) return flash('请粘贴 Cookie-Editor 导出的 Header String');
    setSavingCookie(true);
    try {
      const res = await post<any>('/virtual-products/twitter-config', {
        cookie_header: cookieInput.trim(),
      });
      flash(res.message || 'Cookie 已保存');
      setCookieInput('');
      setShowCookieBox(false);
      loadTwitterConfig();
    } catch (e: any) {
      flash(e?.message || '保存 Cookie 失败');
    } finally {
      setSavingCookie(false);
    }
  };

  const handleClearTwitterCookie = async () => {
    if (!confirm('确认清除 Twitter Cookie？')) return;
    try {
      await del('/virtual-products/twitter-config');
      flash('已清除 Twitter Cookie');
      loadTwitterConfig();
      setShowCookieBox(true);
    } catch (e: any) {
      flash(e?.message || '清除失败');
    }
  };

  const handleSearchTwitter = async () => {
    setSearching(true);
    try {
      const res = await post<any>('/virtual-products/search-twitter', {
        query,
        auto_save: true,
        limit: 10,
      });
      flash(`搜索完成：${res.count || 0} 条，入库 ${res.saved_ids?.length || 0}（模式 ${res.auth_mode}${res.cookie_configured ? ' · Cookie已配' : ' · 未配Cookie'}）`);
      load();
    } catch (e: any) {
      flash(e?.message || 'Twitter 搜索失败');
    } finally {
      setSearching(false);
    }
  };

  const handlePublish = async (id: number) => {
    setPublishingId(id);
    try {
      const res = await post<any>(`/virtual-products/${id}/publish`, {
        cookie_id: selectedCookie || undefined,
      });
      flash(`自动发货已配置：关键词「${res.keyword}」→ 卡密#${res.card_id}`);
      load();
    } catch (e: any) {
      flash(e?.message || '发布失败');
    } finally {
      setPublishingId(null);
    }
  };

  const handleGenerateListing = async (id: number) => {
    setGenListingId(id);
    try {
      const res = await post<any>(`/virtual-products/${id}/generate-listing`, {});
      setListingPreview({ productId: id, listing: res.listing || {} });
      flash('上架文案已生成');
      load();
    } catch (e: any) {
      flash(e?.message || '生成文案失败');
    } finally {
      setGenListingId(null);
    }
  };

  const handlePrepareForSale = async (id: number) => {
    if (!selectedCookie) {
      flash('请先选择要绑定的闲鱼账号');
      return;
    }
    setPreparingId(id);
    try {
      const res = await post<any>(`/virtual-products/${id}/prepare-for-sale`, {
        cookie_id: selectedCookie,
      });
      setListingPreview({
        productId: id,
        listing: res.listing || {},
        next_steps: res.next_steps || res.listing?.manual_steps || [],
      });
      flash(`一键准备完成：卡密#${res.publish?.card_id} · 关键词「${res.publish?.keyword}」· 请复制文案到闲鱼发品`);
      load();
    } catch (e: any) {
      flash(e?.message || '一键准备失败');
    } finally {
      setPreparingId(null);
    }
  };

  const handleListToXianyu = async (id: number) => {
    if (!selectedCookie) {
      flash('请先选择闲鱼账号（用于绑定自动发货）');
      return;
    }
    setListingId(id);
    try {
      flash('正在通过闲管家直接发布到闲鱼…');
      const res = await post<any>(`/virtual-products/${id}/list-to-xianyu`, {
        cookie_id: selectedCookie,
        // 闲管家通道：始终真正发布（不再受旧「仅预填」开关影响）
        publish: true,
        channel: 'xianguanjia',
      });
      setListingPreview({
        productId: id,
        listing: res.listing || {},
        next_steps: res.next_steps || res.listing?.manual_steps || [],
        playwright: res.playwright,
        xianguanjia: res.xianguanjia,
        channel: res.channel,
        message: res.message,
        delivery_ok: res.delivery_ok,
        publish: res.publish,
        xianguanjia_ok: res.xianguanjia_ok,
      });
      if (res.xianguanjia_ok) {
        const xg = res.xianguanjia || {};
        flash(
          `已真正上架到闲鱼：管家商品 ${xg.product_id || '-'}${
            xg.item_id ? ` · 闲鱼ID ${xg.item_id}` : ''
          } · 店铺 ${xg.shop_user || '-'}`
        );
      } else if (res.xianguanjia?.product_id) {
        flash(
          res.message ||
            `已创建到闲管家但未上到闲鱼App（仍待发布）。请登录闲管家后台看失败原因；本系统文案/自动发货仍可用。`
        );
      } else {
        flash(res.message || '上架未完全成功，可查看弹窗详情');
      }
      load();
    } catch (e: any) {
      flash(e?.message || '上架到闲鱼失败');
    } finally {
      setListingId(null);
    }
  };

  const copyText = async (text?: string) => {
    if (!text) return flash('无可复制内容');
    try {
      await navigator.clipboard.writeText(text);
      flash('已复制到剪贴板');
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      flash('已复制到剪贴板');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确认删除该资源？')) return;
    try {
      await del(`/virtual-products/${id}`);
      flash('已删除');
      load();
    } catch (e: any) {
      flash(e?.message || '删除失败');
    }
  };

  const handleGenImage = async (item: VirtualProduct) => {
    try {
      flash('正在生成详情图…');
      const res = await post<any>('/virtual-products/generate-image', {
        product_id: item.id,
        title: item.title,
        prompt: `E-commerce product detail poster for digital product "${item.title}", ${item.intro || ''}, Chinese listing style, vertical 3:4, commercial, no watermark`,
      });
      if (res.image_url) {
        flash('详情图已生成');
        load();
      } else {
        flash('生图未返回 URL，请检查 OneCamera Key');
      }
    } catch (e: any) {
      flash(e?.message || '生图失败');
    }
  };

  const statusBadge = (s?: string) => {
    const map: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-600',
      published: 'bg-green-100 text-green-700',
    };
    return map[s || 'draft'] || map.draft;
  };

  const isTwitterItem = (item: VirtualProduct) => {
    const source = (item.source || '').toLowerCase();
    return source.startsWith('twitter') || item.category === 'Twitter采集';
  };

  const twitterItems = items.filter(isTwitterItem);
  const localItems = items.filter((item) => !isTwitterItem(item));

  const renderResourceTable = (resources: VirtualProduct[], emptyText: string) => (
    resources.length === 0 ? (
      <div className="p-10 text-center text-gray-400 font-medium">{emptyText}</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-50">
              <th className="px-5 py-4 font-bold">商品</th>
              <th className="px-3 py-4 font-bold">来源</th>
              <th className="px-3 py-4 font-bold">资源地址</th>
              <th className="px-3 py-4 font-bold">状态</th>
              <th className="px-5 py-4 font-bold text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {resources.map((item) => (
              <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                <td className="px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="w-14 h-14 rounded-xl bg-gray-100 overflow-hidden flex-none border border-gray-100">
                      {(item.detail_image_url || item.cover_image_url) ? (
                        <img src={item.detail_image_url || item.cover_image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon className="w-5 h-5" /></div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-extrabold text-gray-900 truncate max-w-[280px]">{item.title}</div>
                      {item.listing?.listing_title && (
                        <div className="text-xs text-blue-600 font-bold mt-0.5 truncate max-w-[280px]">
                          上架：{item.listing.listing_title}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1 line-clamp-2 max-w-[320px]">{item.intro || '无介绍'}</div>
                      <div className="text-xs text-yellow-700 font-bold mt-1">
                        ¥{item.listing?.suggest_price ?? item.price ?? 0} · {item.category || '未分类'}
                        {item.publish_keyword ? ` · 关键词:${item.publish_keyword}` : ''}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-4">
                  <div className="text-xs font-bold text-gray-600">{item.source || '-'}</div>
                  {item.source_author && <div className="text-xs text-gray-400">@{item.source_author}</div>}
                  {item.source_url && (
                    <a href={item.source_url} target="_blank" rel="noreferrer" className="text-xs text-blue-500 inline-flex items-center gap-1 mt-1">
                      来源 <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </td>
                <td className="px-3 py-4">
                  {item.resource_url ? (
                    <a href={item.resource_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 break-all max-w-[200px] inline-block line-clamp-2">
                      {item.resource_url}
                    </a>
                  ) : (
                    <span className="text-xs text-red-400">未填资源地址</span>
                  )}
                </td>
                <td className="px-3 py-4">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusBadge(item.status)}`}>
                    {item.status === 'published' ? '已发布' : '草稿'}
                  </span>
                  {item.published_card_id ? (
                    <div className="text-[10px] text-gray-400 mt-1">卡密 #{item.published_card_id}</div>
                  ) : null}
                </td>
                <td className="px-5 py-4">
                  <div className="flex flex-wrap items-center justify-end gap-1.5 max-w-[280px]">
                    <button
                      onClick={() => handleGenerateListing(item.id)}
                      disabled={genListingId === item.id}
                      className="px-2.5 py-1.5 rounded-xl bg-blue-50 text-blue-700 text-xs font-bold hover:bg-blue-100 flex items-center gap-1 disabled:opacity-60"
                      title="生成闲鱼标题/描述/定价"
                    >
                      {genListingId === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                      文案
                    </button>
                    <button
                      onClick={() => handleGenImage(item)}
                      className="px-2.5 py-1.5 rounded-xl bg-purple-50 text-purple-700 text-xs font-bold hover:bg-purple-100"
                      title="OneCamera 生成详情图"
                    >
                      生图
                    </button>
                    {item.listing?.copy_text && (
                      <button
                        onClick={() => {
                          setListingPreview({ productId: item.id, listing: item.listing || {} });
                        }}
                        className="px-2.5 py-1.5 rounded-xl bg-gray-100 text-gray-700 text-xs font-bold hover:bg-gray-200 flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> 预览
                      </button>
                    )}
                    <button
                      onClick={() => handlePrepareForSale(item.id)}
                      disabled={preparingId === item.id}
                      className="px-2.5 py-1.5 rounded-xl bg-[#FFE815] text-black text-xs font-bold hover:brightness-95 flex items-center gap-1 disabled:opacity-60"
                      title="生成文案 + 配置自动发货"
                    >
                      {preparingId === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                      一键准备
                    </button>
                    <button
                      onClick={() => handleListToXianyu(item.id)}
                      disabled={listingId === item.id}
                      className="px-2.5 py-1.5 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 flex items-center gap-1 disabled:opacity-60"
                      title="Playwright 打开闲鱼发布页并预填"
                    >
                      {listingId === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3 h-3" />}
                      上架闲鱼
                    </button>
                    <button
                      onClick={() => handlePublish(item.id)}
                      disabled={publishingId === item.id}
                      className="px-2.5 py-1.5 rounded-xl bg-black text-white text-xs font-bold hover:bg-gray-800 flex items-center gap-1 disabled:opacity-60"
                      title="仅配置卡密发货"
                    >
                      {publishingId === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      发货
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded-xl hover:bg-red-50 text-gray-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-3">
            <Package className="w-8 h-8 text-yellow-500" /> 虚拟资源库
          </h2>
          <p className="text-gray-500 mt-2 font-medium">
            采集资源 → 生成闲鱼文案 → 配置自动发货 → 复制到闲鱼 App 发品
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={load} className="px-4 py-2.5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:bg-gray-50 flex items-center gap-2 text-sm font-bold">
            <RefreshCw className="w-4 h-4" /> 刷新
          </button>
          <button onClick={() => setShowForm(true)} className="px-4 py-2.5 rounded-2xl bg-[#FFE815] text-black shadow-lg shadow-yellow-100 flex items-center gap-2 text-sm font-bold">
            <Plus className="w-4 h-4" /> 手动添加
          </button>
        </div>
      </div>

      {msg && (
        <div className="p-3 rounded-2xl bg-yellow-50 border border-yellow-100 text-sm font-bold text-gray-800">
          {msg}
        </div>
      )}

      {/* Twitter cookie config */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 space-y-3">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <div className="font-extrabold text-gray-900 flex items-center gap-2">
              <Twitter className="w-5 h-5" /> Twitter Cookie（Agent Reach / twitter-cli）
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {twConfigured
                ? `已配置 auth_token：${twPreview}（无需 Bearer，用 Cookie 即可搜）`
                : '未配置 — 搜索将降级为网页回退，准确度较差'}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowCookieBox((v) => !v)}
              className="px-4 py-2 rounded-2xl bg-gray-900 text-white text-sm font-bold"
            >
              {showCookieBox ? '收起' : (twConfigured ? '更新 Cookie' : '填写 Cookie')}
            </button>
            {twConfigured && (
              <button onClick={handleClearTwitterCookie} className="px-4 py-2 rounded-2xl bg-red-50 text-red-600 text-sm font-bold">
                清除
              </button>
            )}
          </div>
        </div>

        {showCookieBox && (
          <div className="space-y-3 pt-2 border-t border-gray-50">
            <ol className="text-xs text-gray-500 space-y-1 list-decimal list-inside leading-relaxed">
              <li>Chrome 安装 <a className="text-blue-600 underline" href="https://chromewebstore.google.com/detail/cookie-editor/hlkenndednhfkekhgcdicdfddnkalmdm" target="_blank" rel="noreferrer">Cookie-Editor</a></li>
              <li>浏览器登录 <a className="text-blue-600 underline" href="https://x.com" target="_blank" rel="noreferrer">x.com</a>（建议专用小号）</li>
              <li>点 Cookie-Editor → Export → <b>Header String</b></li>
              <li>整段粘贴到下方保存（会解析 auth_token + ct0）</li>
            </ol>
            <textarea
              value={cookieInput}
              onChange={(e) => setCookieInput(e.target.value)}
              placeholder="auth_token=xxxx; ct0=yyyy; ...  （Cookie-Editor Header String）"
              className="w-full min-h-[100px] p-4 rounded-2xl bg-gray-50 border border-gray-100 text-xs font-mono"
            />
            <button
              onClick={handleSaveTwitterCookie}
              disabled={savingCookie}
              className="px-5 py-2.5 rounded-2xl bg-[#FFE815] text-black font-bold text-sm disabled:opacity-60 flex items-center gap-2"
            >
              {savingCookie ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              保存 Twitter Cookie
            </button>
          </div>
        )}
      </div>

      {/* Twitter search bar */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Twitter 搜索词，如：虚拟资源 源码 软件 网盘"
              className="w-full h-12 pl-11 pr-4 rounded-2xl bg-gray-50 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-300 font-medium"
            />
          </div>
          <button
            onClick={handleSearchTwitter}
            disabled={searching}
            className="h-12 px-6 rounded-2xl bg-black text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Twitter className="w-4 h-4" />}
            搜索并入库
          </button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
          <span>发布绑定账号：</span>
          <select
            value={selectedCookie}
            onChange={(e) => setSelectedCookie(e.target.value)}
            className="rounded-xl border border-gray-200 px-3 py-1.5 bg-gray-50 font-medium"
          >
            <option value="">不指定账号</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.nickname || a.remark || a.id}</option>
            ))}
          </select>
          <span className="text-emerald-700 font-bold text-xs">「上架闲鱼」= 闲管家 API 直接发布到闲鱼店铺</span>
        </div>
      </div>

      {/* Form modal-ish */}
      {showForm && (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 space-y-4">
          <h3 className="font-extrabold text-lg">添加虚拟资源</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <input className="h-11 px-4 rounded-xl bg-gray-50 border border-gray-100" placeholder="标题" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <input className="h-11 px-4 rounded-xl bg-gray-50 border border-gray-100" placeholder="分类" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <input className="h-11 px-4 rounded-xl bg-gray-50 border border-gray-100" type="number" placeholder="价格" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} />
            <input className="h-11 px-4 rounded-xl bg-gray-50 border border-gray-100" placeholder="发货关键词（匹配商品标题）" value={form.publish_keyword} onChange={(e) => setForm({ ...form, publish_keyword: e.target.value })} />
            <input className="h-11 px-4 rounded-xl bg-gray-50 border border-gray-100 md:col-span-2" placeholder="资源下载/访问地址" value={form.resource_url} onChange={(e) => setForm({ ...form, resource_url: e.target.value })} />
            <input className="h-11 px-4 rounded-xl bg-gray-50 border border-gray-100 md:col-span-2" placeholder="详情图 URL" value={form.detail_image_url} onChange={(e) => setForm({ ...form, detail_image_url: e.target.value })} />
            <input className="h-11 px-4 rounded-xl bg-gray-50 border border-gray-100 md:col-span-2" placeholder="本地路径（可选）" value={form.local_path} onChange={(e) => setForm({ ...form, local_path: e.target.value })} />
            <textarea className="min-h-[90px] p-4 rounded-xl bg-gray-50 border border-gray-100 md:col-span-2" placeholder="商品介绍" value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} className="px-5 py-2.5 rounded-2xl bg-[#FFE815] font-bold flex items-center gap-2"><Upload className="w-4 h-4" />保存</button>
            <button onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-2xl bg-gray-100 font-bold">取消</button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-yellow-500" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-16 text-center text-gray-400 font-medium">暂无资源，可搜索 Twitter 或手动添加 / 导入本地项目</div>
        ) : (
          <>
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between gap-3">
              <div className="font-extrabold text-gray-900 flex items-center gap-2">
                <Twitter className="w-4 h-4" /> Twitter 搜索结果
              </div>
              <span className="text-xs font-bold text-gray-400">{twitterItems.length} 条</span>
            </div>
            {twitterItems.length === 0 ? (
              <div className="p-10 text-center text-gray-400 font-medium">暂无 Twitter 搜索结果</div>
            ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-50">
                  <th className="px-5 py-4 font-bold">商品</th>
                  <th className="px-3 py-4 font-bold">来源</th>
                  <th className="px-3 py-4 font-bold">资源地址</th>
                  <th className="px-3 py-4 font-bold">状态</th>
                  <th className="px-5 py-4 font-bold text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {twitterItems.map((item) => (
                  <tr key={item.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-start gap-3">
                        <div className="w-14 h-14 rounded-xl bg-gray-100 overflow-hidden flex-none border border-gray-100">
                          {(item.detail_image_url || item.cover_image_url) ? (
                            <img src={item.detail_image_url || item.cover_image_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon className="w-5 h-5" /></div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-extrabold text-gray-900 truncate max-w-[280px]">{item.title}</div>
                          {item.listing?.listing_title && (
                            <div className="text-xs text-blue-600 font-bold mt-0.5 truncate max-w-[280px]">
                              上架：{item.listing.listing_title}
                            </div>
                          )}
                          <div className="text-xs text-gray-400 mt-1 line-clamp-2 max-w-[320px]">{item.intro || '无介绍'}</div>
                          <div className="text-xs text-yellow-700 font-bold mt-1">
                            ¥{item.listing?.suggest_price ?? item.price ?? 0} · {item.category || '未分类'}
                            {item.publish_keyword ? ` · 关键词:${item.publish_keyword}` : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4">
                      <div className="text-xs font-bold text-gray-600">{item.source || '-'}</div>
                      {item.source_author && <div className="text-xs text-gray-400">@{item.source_author}</div>}
                      {item.source_url && (
                        <a href={item.source_url} target="_blank" rel="noreferrer" className="text-xs text-blue-500 inline-flex items-center gap-1 mt-1">
                          来源 <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-4">
                      {item.resource_url ? (
                        <a href={item.resource_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 break-all max-w-[200px] inline-block line-clamp-2">
                          {item.resource_url}
                        </a>
                      ) : (
                        <span className="text-xs text-red-400">未填资源地址</span>
                      )}
                    </td>
                    <td className="px-3 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusBadge(item.status)}`}>
                        {item.status === 'published' ? '已发布' : '草稿'}
                      </span>
                      {item.published_card_id ? (
                        <div className="text-[10px] text-gray-400 mt-1">卡密 #{item.published_card_id}</div>
                      ) : null}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap items-center justify-end gap-1.5 max-w-[280px]">
                        <button
                          onClick={() => handleGenerateListing(item.id)}
                          disabled={genListingId === item.id}
                          className="px-2.5 py-1.5 rounded-xl bg-blue-50 text-blue-700 text-xs font-bold hover:bg-blue-100 flex items-center gap-1 disabled:opacity-60"
                          title="生成闲鱼标题/描述/定价"
                        >
                          {genListingId === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                          文案
                        </button>
                        <button
                          onClick={() => handleGenImage(item)}
                          className="px-2.5 py-1.5 rounded-xl bg-purple-50 text-purple-700 text-xs font-bold hover:bg-purple-100"
                          title="OneCamera 生成详情图"
                        >
                          生图
                        </button>
                        {item.listing?.copy_text && (
                          <button
                            onClick={() => {
                              setListingPreview({ productId: item.id, listing: item.listing || {} });
                            }}
                            className="px-2.5 py-1.5 rounded-xl bg-gray-100 text-gray-700 text-xs font-bold hover:bg-gray-200 flex items-center gap-1"
                          >
                            <Copy className="w-3 h-3" /> 预览
                          </button>
                        )}
                        <button
                          onClick={() => handlePrepareForSale(item.id)}
                          disabled={preparingId === item.id}
                          className="px-2.5 py-1.5 rounded-xl bg-[#FFE815] text-black text-xs font-bold hover:brightness-95 flex items-center gap-1 disabled:opacity-60"
                          title="生成文案 + 配置自动发货"
                        >
                          {preparingId === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                          一键准备
                        </button>
                        <button
                          onClick={() => handleListToXianyu(item.id)}
                          disabled={listingId === item.id}
                          className="px-2.5 py-1.5 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 flex items-center gap-1 disabled:opacity-60"
                          title="Playwright 打开闲鱼发布页并预填"
                        >
                          {listingId === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3 h-3" />}
                          上架闲鱼
                        </button>
                        <button
                          onClick={() => handlePublish(item.id)}
                          disabled={publishingId === item.id}
                          className="px-2.5 py-1.5 rounded-xl bg-black text-white text-xs font-bold hover:bg-gray-800 flex items-center gap-1 disabled:opacity-60"
                          title="仅配置卡密发货"
                        >
                          {publishingId === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                          发货
                        </button>
                        <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded-xl hover:bg-red-50 text-gray-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            )}
          </div>
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between gap-3">
              <div className="font-extrabold text-gray-900 flex items-center gap-2">
                <Store className="w-4 h-4" /> 本地资源
              </div>
              <span className="text-xs font-bold text-gray-400">{localItems.length} 条</span>
            </div>
            {renderResourceTable(localItems, '暂无本地资源')}
          </div>
          </>
        )}
      </div>

      {/* Listing / publish result modal */}
      {listingPreview && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setListingPreview(null)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-xl font-extrabold text-gray-900">
                  {listingPreview.xianguanjia_ok
                    ? '已真正上架到闲鱼'
                    : listingPreview.xianguanjia?.product_id
                      ? '已创建到闲管家，但未上到闲鱼'
                      : '闲鱼上架结果'}
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  {listingPreview.xianguanjia_ok
                    ? '闲鱼 App 侧应可看到该商品（可能有短暂延迟）。'
                    : listingPreview.xianguanjia?.product_id
                      ? '管家侧有记录，但异步发布未成功（常见原因：试用版/未开鱼小铺/类目审核失败）。请到闲管家后台「商品」查看失败原因。'
                      : '若未上架成功，可复制文案到闲鱼 App 手动发品；标题务必含发货关键词'}
                </p>
              </div>
              <button onClick={() => setListingPreview(null)} className="text-gray-400 hover:text-gray-700 text-sm font-bold">关闭</button>
            </div>

            <div className="grid gap-3 text-sm">
              {(listingPreview.xianguanjia_ok || listingPreview.xianguanjia) && (
                <div className={`p-4 rounded-2xl border text-sm space-y-1.5 ${
                  listingPreview.xianguanjia_ok
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-amber-200 bg-amber-50'
                }`}>
                  <div className={`font-extrabold text-base ${
                    listingPreview.xianguanjia_ok ? 'text-emerald-900' : 'text-amber-900'
                  }`}>
                    {listingPreview.xianguanjia_ok
                      ? '✅ 已上架到闲鱼'
                      : '⚠️ 仅创建到闲管家，闲鱼App未见属正常（异步未成功）'}
                  </div>
                  <div>管家商品ID：<b>{listingPreview.xianguanjia?.product_id || '-'}</b></div>
                  <div>闲鱼 item_id：<b>{listingPreview.xianguanjia?.item_id || 0}</b>（0 表示还没同步到闲鱼）</div>
                  <div>发布店铺：<b>{listingPreview.xianguanjia?.shop_user || '-'}</b></div>
                  <div>
                    真正上架：{String(!!listingPreview.xianguanjia?.publish_ok)} · 类目：
                    {listingPreview.xianguanjia?.category?.name || '-'}
                  </div>
                  {listingPreview.delivery_ok && listingPreview.publish?.card_id && (
                    <div className="font-bold text-gray-800">
                      本系统自动发货已配置 · 卡密#{listingPreview.publish.card_id}
                      {listingPreview.publish.keyword ? ` · 关键词「${listingPreview.publish.keyword}」` : ''}
                    </div>
                  )}
                  <div className="text-gray-700">{listingPreview.xianguanjia?.message || listingPreview.message}</div>
                  {!listingPreview.xianguanjia_ok && (
                    <div className="text-amber-950 font-bold pt-1 space-y-1">
                      <div>请登录 https://goofish.pro → 商品列表，看该商品是否在「处理中/失败」并查看失败原因。</div>
                      <div>当前店铺账号多为试用且未开鱼小铺时，API 可创建但常无法真正推到闲鱼 App。</div>
                    </div>
                  )}
                </div>
              )}

              <div className="p-3 rounded-2xl bg-yellow-50 border border-yellow-100">
                <div className="text-xs text-gray-500 font-bold mb-1">商品标题</div>
                <div className="font-extrabold text-gray-900">{listingPreview.listing.listing_title}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-2xl bg-gray-50">
                  <div className="text-xs text-gray-500 font-bold">价格</div>
                  <div className="font-extrabold text-lg">¥{listingPreview.listing.suggest_price}</div>
                </div>
                <div className="p-3 rounded-2xl bg-gray-50">
                  <div className="text-xs text-gray-500 font-bold">发货关键词</div>
                  <div className="font-extrabold text-blue-700">{listingPreview.listing.publish_keyword}</div>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-gray-50">
                <div className="text-xs text-gray-500 font-bold mb-1">描述（已用于发布）</div>
                <pre className="whitespace-pre-wrap text-xs text-gray-700 font-medium leading-relaxed">{listingPreview.listing.listing_description}</pre>
              </div>
              {listingPreview.listing.tags && (
                <div className="flex flex-wrap gap-1.5">
                  {listingPreview.listing.tags.map((t) => (
                    <span key={t} className="px-2 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-bold">#{t}</span>
                  ))}
                </div>
              )}
              {/* 仅在未直接发布时展示手动步骤 */}
              {!(listingPreview.xianguanjia_ok || listingPreview.xianguanjia?.publish_ok) &&
                (listingPreview.next_steps || listingPreview.listing.manual_steps) && (
                <div className="p-3 rounded-2xl bg-black text-white text-xs space-y-1">
                  <div className="font-bold mb-2">手动上架步骤（备用）</div>
                  {(listingPreview.next_steps || listingPreview.listing.manual_steps || []).map((s, i) => (
                    <div key={i}>{s}</div>
                  ))}
                </div>
              )}
              {listingPreview.playwright && (
                <div className={`p-3 rounded-2xl border text-xs space-y-1 ${
                  listingPreview.playwright.success
                    ? 'bg-emerald-50 border-emerald-100'
                    : 'bg-amber-50 border-amber-200'
                }`}>
                  <div className={`font-bold ${listingPreview.playwright.success ? 'text-emerald-800' : 'text-amber-800'}`}>
                    网页自动上架结果
                  </div>
                  <div className="text-gray-700">{listingPreview.playwright.message}</div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <button
                onClick={() => setListingPreview(null)}
                className="px-5 py-2.5 rounded-2xl bg-emerald-600 text-white font-bold text-sm"
              >
                知道了
              </button>
              <button
                onClick={() => copyText(listingPreview.listing.copy_text)}
                className="px-4 py-2.5 rounded-2xl bg-gray-100 font-bold text-sm flex items-center gap-2"
              >
                <Copy className="w-4 h-4" /> 复制文案（备用）
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-xs text-gray-400 leading-relaxed p-4 rounded-2xl bg-white border border-gray-50">
        <div className="font-bold text-gray-600 mb-1 flex items-center gap-1"><Store className="w-3.5 h-3.5" /> 上架说明</div>
        <b>文案</b>：生成闲鱼标题/描述/定价/关键词。
        <b>一键准备</b>：文案 + 卡密 + 发货规则（不发到闲鱼）。
        <b>上架闲鱼</b>：经闲管家 API <b>直接创建并上架</b>到已授权店铺（异步），并配置本系统自动发货。
        <b>发货</b>：仅配置自动发货规则。
      </div>
    </div>
  );
};

export default VirtualResources;
